<template>
  <div ref="dashRoot" class="alarm-dash">
    <div class="content">
      <div class="period-bar">
        <span class="period-label">统计周期：</span>
        <div class="seg-tabs">
          <div class="seg-tab" :class="{ active: period === 'month' }" @click="period = 'month'; onPeriodChange()">月度</div>
          <div class="seg-tab" :class="{ active: period === 'quarter' }" @click="period = 'quarter'; onPeriodChange()">季度</div>
          <div class="seg-tab" :class="{ active: period === 'year' }" @click="period = 'year'; onPeriodChange()">年度</div>
        </div>
        <el-date-picker
          v-if="period === 'month'"
          v-model="dateVal"
          type="month"
          placeholder="选择月份"
          format="YYYY-MM"
          value-format="YYYY-MM"
          size="default"
          @change="onPeriodChange"
        />
        <el-select
          v-if="period === 'quarter'"
          v-model="quarterVal"
          placeholder="选择季度"
          size="default"
          style="width: 200px"
          @change="onPeriodChange"
        >
          <el-option v-for="q in quarterOptions" :key="q.value" :label="q.label" :value="q.value" />
        </el-select>
        <el-date-picker
          v-if="period === 'year'"
          v-model="dateVal"
          type="year"
          placeholder="选择年份"
          format="YYYY"
          value-format="YYYY"
          size="default"
          @change="onPeriodChange"
        />
        <div style="flex: 1"></div>
        <v-btn
          color="primary"
          :loading="loading"
          @click="refreshData"
          class="text-none"
          height="38"
          density="compact"
          prepend-icon="mdi-magnify"
        >
          查询
        </v-btn>

      </div>

      <div class="stats-row">
        <div class="stat-box total">
          <div class="sb-icon"><span class="mdi mdi-bell-outline"></span></div>
          <div class="sb-body">
            <div class="sb-val">{{ summary.total }}</div>
            <div class="sb-label">{{ periodLabel }}告警总数</div>
          </div>
          <div class="sb-trend" :class="summary.totalTrend >= 0 ? 'up' : 'down'">
            {{ summary.totalTrend >= 0 ? '+' : '' }}{{ summary.totalTrend }}%
          </div>
        </div>
        <div class="stat-box power">
          <div class="sb-icon"><span class="mdi mdi-flash-alert"></span></div>
          <div class="sb-body">
            <div class="sb-val">{{ catTotals.power }}</div>
            <div class="sb-label">功率异常</div>
          </div>
          <div class="sb-trend" :class="summary.powerTrend >= 0 ? 'up' : 'down'">
            {{ summary.powerTrend >= 0 ? '+' : '' }}{{ summary.powerTrend }}%
          </div>
        </div>
        <div class="stat-box comm">
          <div class="sb-icon"><span class="mdi mdi-wifi-off"></span></div>
          <div class="sb-body">
            <div class="sb-val">{{ catTotals.comm }}</div>
            <div class="sb-label">通信异常</div>
          </div>
          <div class="sb-trend" :class="summary.commTrend >= 0 ? 'up' : 'down'">
            {{ summary.commTrend >= 0 ? '+' : '' }}{{ summary.commTrend }}%
          </div>
        </div>
        <div class="stat-box volt">
          <div class="sb-icon"><span class="mdi mdi-battery-alert"></span></div>
          <div class="sb-body">
            <div class="sb-val">{{ catTotals.volt }}</div>
            <div class="sb-label">电压异常</div>
          </div>
          <div class="sb-trend" :class="summary.voltTrend >= 0 ? 'up' : 'down'">
            {{ summary.voltTrend >= 0 ? '+' : '' }}{{ summary.voltTrend }}%
          </div>
        </div>
        <div class="stat-box temp">
          <div class="sb-icon"><span class="mdi mdi-thermometer-alert"></span></div>
          <div class="sb-body">
            <div class="sb-val">{{ catTotals.temp }}</div>
            <div class="sb-label">温度异常</div>
          </div>
          <div class="sb-trend" :class="summary.tempTrend >= 0 ? 'up' : 'down'">
            {{ summary.tempTrend >= 0 ? '+' : '' }}{{ summary.tempTrend }}%
          </div>
        </div>
      </div>

      <div class="row-2">
        <div class="card">
          <div class="card-head">电站告警数量排名 <span class="sub">Top 10</span></div>
          <div ref="chartStationEl" class="chart-box"></div>
        </div>
        <div class="card">
          <div class="card-head">告警类型排名 <span class="sub">按数量降序</span></div>
          <ul class="rank-list">
            <li v-for="(item, idx) in alarmTypeStats" :key="item.code" class="rank-item">
              <span class="rk">{{ idx + 1 }}</span>
              <div class="rk-info">
                <div class="rk-name">
                  <span class="cat-dot" :style="{ background: catColorVar[item.cat] }"></span>{{ item.name }}
                </div>
                <div class="rk-bar-wrap">
                  <div class="rk-bar" :style="{ width: item.pct + '%', background: catColorVar[item.cat] }"></div>
                </div>
              </div>
              <span class="rk-val" :style="{ color: catColorVar[item.cat] }">{{ item.count }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import * as echarts from 'echarts'
import { ElMessage } from 'element-plus'

const props = defineProps<{
  active?: boolean
}>()

type CatKey = 'power' | 'comm' | 'volt' | 'temp'

interface AlarmStatRow {
  code: string
  name: string
  count: number
  cat: CatKey
  pct?: number
}

interface StationRankRow {
  name: string
  count: number
}

interface SummaryShape {
  total: number
  totalTrend: number
  powerTrend: number
  commTrend: number
  voltTrend: number
  tempTrend: number
}

interface PeriodData {
  summary: SummaryShape
  stats: Omit<AlarmStatRow, 'pct'>[]
  stationRank: StationRankRow[]
}

const loading = ref(false)
const period = ref<'month' | 'quarter' | 'year'>('month')
const now = new Date()
const pad = (n: number) => String(n).padStart(2, '0')
const dateVal = ref(`${now.getFullYear()}-${pad(now.getMonth() + 1)}`)
const quarterVal = ref(`${now.getFullYear()}-Q${Math.floor(now.getMonth() / 3) + 1}`)
const quarterOptions: { label: string; value: string }[] = []
const y = now.getFullYear()
;[
  { l: 'Q1 第一季度', v: `${y}-Q1` },
  { l: 'Q2 第二季度', v: `${y}-Q2` },
  { l: 'Q3 第三季度', v: `${y}-Q3` },
  { l: 'Q4 第四季度', v: `${y}-Q4` },
  { l: `${y - 1} Q1`, v: `${y - 1}-Q1` },
  { l: `${y - 1} Q2`, v: `${y - 1}-Q2` },
  { l: `${y - 1} Q3`, v: `${y - 1}-Q3` },
  { l: `${y - 1} Q4`, v: `${y - 1}-Q4` },
].forEach((q) => quarterOptions.push({ label: q.l, value: q.v }))

/** 与 .alarm-dash 上 --cat-* 对齐，供内联样式引用主题色 */
const catColorVar: Record<CatKey, string> = {
  power: 'var(--cat-power)',
  comm: 'var(--cat-comm)',
  volt: 'var(--cat-volt)',
  temp: 'var(--cat-temp)',
}

const monthData: PeriodData = {
  summary: { total: 485, totalTrend: 12, powerTrend: 8, commTrend: -5, voltTrend: -18, tempTrend: 22 },
  stats: [
    { code: 'A0_00_04_0002', name: '设备离线', count: 128, cat: 'comm' },
    { code: 'A0_00_06_0002', name: '最高温度超限', count: 82, cat: 'temp' },
    { code: 'A0_00_03_0002', name: '超需量告警', count: 67, cat: 'power' },
    { code: 'A0_00_04_0001', name: '整站离线', count: 53, cat: 'comm' },
    { code: 'A0_00_05_0001', name: '电芯压差超限', count: 44, cat: 'volt' },
    { code: 'A0_00_06_0001', name: '温差告警', count: 36, cat: 'temp' },
    { code: 'A0_00_04_0003', name: '并网柜失压', count: 28, cat: 'comm' },
    { code: 'A0_00_03_0001', name: '逆流告警', count: 21, cat: 'power' },
    { code: 'A0_00_05_0002', name: '电芯过压', count: 14, cat: 'volt' },
    { code: 'A0_00_03_0003', name: '非计划停机告警', count: 8, cat: 'power' },
    { code: 'A0_00_05_0003', name: '电芯欠压', count: 4, cat: 'volt' },
  ],
  stationRank: [
    { name: '广东深圳宝安储能电站', count: 112 },
    { name: '浙江杭州余杭储能电站', count: 93 },
    { name: '江苏南京江宁储能电站', count: 74 },
    { name: '中晟新能源—浙江华氏钢业', count: 65 },
    { name: '浙江恒星传动科技储能项目', count: 52 },
    { name: '上海浦东张江储能电站', count: 40 },
    { name: '安徽合肥高新储能电站', count: 30 },
    { name: '山东济南历城储能电站', count: 19 },
    { name: '湖南长沙岳麓储能电站', count: 12 },
    { name: '福建厦门思明储能电站', count: 6 },
  ],
}

const quarterData: PeriodData = {
  summary: { total: 1520, totalTrend: -3, powerTrend: -1, commTrend: -8, voltTrend: 5, tempTrend: 12 },
  stats: [
    { code: 'A0_00_04_0002', name: '设备离线', count: 398, cat: 'comm' },
    { code: 'A0_00_06_0002', name: '最高温度超限', count: 265, cat: 'temp' },
    { code: 'A0_00_03_0002', name: '超需量告警', count: 210, cat: 'power' },
    { code: 'A0_00_04_0001', name: '整站离线', count: 162, cat: 'comm' },
    { code: 'A0_00_05_0001', name: '电芯压差超限', count: 138, cat: 'volt' },
    { code: 'A0_00_06_0001', name: '温差告警', count: 112, cat: 'temp' },
    { code: 'A0_00_04_0003', name: '并网柜失压', count: 85, cat: 'comm' },
    { code: 'A0_00_03_0001', name: '逆流告警', count: 62, cat: 'power' },
    { code: 'A0_00_05_0002', name: '电芯过压', count: 46, cat: 'volt' },
    { code: 'A0_00_03_0003', name: '非计划停机告警', count: 28, cat: 'power' },
    { code: 'A0_00_05_0003', name: '电芯欠压', count: 14, cat: 'volt' },
  ],
  stationRank: [
    { name: '广东深圳宝安储能电站', count: 346 },
    { name: '浙江杭州余杭储能电站', count: 292 },
    { name: '江苏南京江宁储能电站', count: 231 },
    { name: '中晟新能源—浙江华氏钢业', count: 204 },
    { name: '浙江恒星传动科技储能项目', count: 165 },
    { name: '上海浦东张江储能电站', count: 128 },
    { name: '安徽合肥高新储能电站', count: 95 },
    { name: '山东济南历城储能电站', count: 59 },
    { name: '湖南长沙岳麓储能电站', count: 38 },
    { name: '福建厦门思明储能电站', count: 22 },
  ],
}

const yearData: PeriodData = {
  summary: { total: 6320, totalTrend: 7, powerTrend: 3, commTrend: -2, voltTrend: 15, tempTrend: 18 },
  stats: [
    { code: 'A0_00_04_0002', name: '设备离线', count: 1680, cat: 'comm' },
    { code: 'A0_00_06_0002', name: '最高温度超限', count: 1080, cat: 'temp' },
    { code: 'A0_00_03_0002', name: '超需量告警', count: 860, cat: 'power' },
    { code: 'A0_00_04_0001', name: '整站离线', count: 660, cat: 'comm' },
    { code: 'A0_00_05_0001', name: '电芯压差超限', count: 560, cat: 'volt' },
    { code: 'A0_00_06_0001', name: '温差告警', count: 450, cat: 'temp' },
    { code: 'A0_00_04_0003', name: '并网柜失压', count: 340, cat: 'comm' },
    { code: 'A0_00_03_0001', name: '逆流告警', count: 252, cat: 'power' },
    { code: 'A0_00_05_0002', name: '电芯过压', count: 186, cat: 'volt' },
    { code: 'A0_00_03_0003', name: '非计划停机告警', count: 112, cat: 'power' },
    { code: 'A0_00_05_0003', name: '电芯欠压', count: 42, cat: 'volt' },
  ],
  stationRank: [
    { name: '广东深圳宝安储能电站', count: 1420 },
    { name: '浙江杭州余杭储能电站', count: 1210 },
    { name: '江苏南京江宁储能电站', count: 960 },
    { name: '中晟新能源—浙江华氏钢业', count: 840 },
    { name: '浙江恒星传动科技储能项目', count: 680 },
    { name: '上海浦东张江储能电站', count: 530 },
    { name: '安徽合肥高新储能电站', count: 395 },
    { name: '山东济南历城储能电站', count: 285 },
    { name: '湖南长沙岳麓储能电站', count: 180 },
    { name: '福建厦门思明储能电站', count: 95 },
  ],
}

const periodLabel = computed(() => ({ month: '本月', quarter: '本季度', year: '本年度' }[period.value]))

function currentData(): PeriodData {
  return { month: monthData, quarter: quarterData, year: yearData }[period.value]
}

const summary = ref<SummaryShape>({ ...monthData.summary })
const alarmTypeStats = ref<AlarmStatRow[]>(monthData.stats.map((a) => ({ ...a })))
const stationRank = ref<StationRankRow[]>(monthData.stationRank.map((s) => ({ ...s })))

const catTotals = computed(() => {
  const t = { power: 0, comm: 0, volt: 0, temp: 0 }
  alarmTypeStats.value.forEach((a) => {
    t[a.cat] += a.count
  })
  return t
})

function recalcPct() {
  const mx = Math.max(...alarmTypeStats.value.map((a) => a.count), 1)
  alarmTypeStats.value.forEach((a) => {
    a.pct = Math.round((a.count / mx) * 100)
  })
}
recalcPct()

const dashRoot = ref<HTMLElement | null>(null)
const chartStationEl = ref<HTMLElement | null>(null)
let stationChart: echarts.ECharts | null = null

function chartPalette(): { primary: string; gradientEnd: string; axisMuted: string; split: string } {
  const root = typeof document !== 'undefined' ? document.documentElement : null
  const host = dashRoot.value
  const read = (el: HTMLElement | null, name: string, fb: string) => {
    if (!el) return fb
    const v = getComputedStyle(el).getPropertyValue(name).trim()
    return v || fb
  }
  const primary = read(root, '--color-primary', '#2B57EE')
  const gradientEnd = read(host, '--chart-gradient-end', '#48a9a6')
  const axisMuted = read(host, '--chart-axis-label', '#7a8199')
  const split = read(host, '--chart-split-line', '#edf2f6')
  return { primary, gradientEnd, axisMuted, split }
}

function setStationChart() {
  if (!stationChart) return
  const d = stationRank.value
  const { primary, gradientEnd, axisMuted, split } = chartPalette()
  stationChart.setOption({
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '10%', bottom: '3%', top: '3%', containLabel: true },
    xAxis: { type: 'value', axisLabel: { color: axisMuted }, splitLine: { lineStyle: { color: split } } },
    yAxis: {
      type: 'category',
      data: d.map((s) => (s.name.length > 8 ? s.name.slice(0, 8) + '..' : s.name)).reverse(),
      axisLabel: { color: axisMuted, fontSize: 11 },
    },
    series: [
      {
        type: 'bar',
        data: d.map((s) => s.count).reverse(),
        barWidth: '55%',
        itemStyle: {
          borderRadius: [0, 6, 6, 0],
          color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
            { offset: 0, color: primary },
            { offset: 1, color: gradientEnd },
          ]),
        },
        label: { show: true, position: 'right', color: axisMuted, fontSize: 11 },
      },
    ],
  })
}

function onResize() {
  stationChart?.resize()
}

function initCharts() {
  if (!chartStationEl.value) return
  stationChart = echarts.init(chartStationEl.value)
  setStationChart()
  window.addEventListener('resize', onResize)
}

function disposeCharts() {
  window.removeEventListener('resize', onResize)
  stationChart?.dispose()
  stationChart = null
}

function onPeriodChange() {
  const src = currentData()
  summary.value = { ...src.summary }
  alarmTypeStats.value = src.stats.map((a) => ({ ...a }))
  stationRank.value = src.stationRank.map((s) => ({ ...s }))
  recalcPct()
  nextTick(() => setStationChart())
}

function refreshData() {
  loading.value = true
  setTimeout(() => {
    onPeriodChange()
    loading.value = false
    ElMessage.success('数据已刷新')
  }, 500)
}

onMounted(() => {
  nextTick(() => initCharts())
})

onUnmounted(() => {
  disposeCharts()
})

watch(
  () => props.active,
  (v) => {
    if (v) nextTick(() => stationChart?.resize())
  },
)
</script>

<style scoped lang="scss">
/* 与 AlarmConfig.vue、variables.scss / gm-card 体系统一 */
.alarm-dash {
  --primary: var(--color-primary);
  --primary-light: color-mix(in srgb, var(--color-primary) 14%, #ffffff);
  --primary-faint: color-mix(in srgb, var(--color-primary) 8%, #f4f6fd);
  --secondary: #48a9a6;
  --success: var(--color-success);
  --warning: var(--color-warning);
  --danger: var(--color-error);
  --info: var(--color-info);
  --bg: transparent;
  --card-bg: rgba(255, 255, 255, 0.82);
  --text-primary: var(--color-text);
  --text-secondary: color-mix(in srgb, var(--color-text) 78%, #7a8199);
  --text-muted: color-mix(in srgb, var(--color-text) 55%, #a0a6bd);
  --border: #e9efff;
  --surface-muted: #eef1fb;
  --shadow-card: 0 2px 8px rgba(8, 40, 87, 0.05);
  --cat-power: var(--color-warning);
  --cat-comm: var(--color-primary);
  --cat-volt: #e53935;
  --cat-temp: #7c3aed;
  --chart-gradient-end: #48a9a6;
  --chart-axis-label: color-mix(in srgb, var(--color-text) 72%, #7a8199);
  --chart-split-line: var(--color-td-border);
  min-height: 0;
  background: var(--bg);
  color: var(--text-primary);
  border-radius: 0;
}

.content {
  padding: 16px 20px 20px;
  max-width: 100%;
  box-sizing: border-box;
}

.period-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid #fff;
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(6px);
  padding: 16px 20px;
}

.seg-tabs {
  display: inline-flex;
  background: var(--surface-muted);
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
  border: 1px solid color-mix(in srgb, var(--color-primary) 8%, var(--border));
}

.seg-tab {
  padding: 8px 20px;
  font-size: 13px;
  color: var(--text-secondary);
  border-radius: 8px;
  cursor: pointer;
  transition:
    color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
  user-select: none;
}

.seg-tab:hover {
  color: var(--text-primary);
}

.seg-tab.active {
  background: #fff;
  color: var(--primary);
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(43, 87, 238, 0.1);
}

.period-bar :deep(.el-date-editor) {
  width: 180px;
}

.period-bar .period-label {
  font-size: 13px;
  color: var(--text-muted);
  white-space: nowrap;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 16px;
}

.stat-box {
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid #fff;
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(6px);
  padding: 18px 20px;
  position: relative;
  overflow: hidden;
  display: flex;
  gap: 14px;
  align-items: center;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.stat-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 14px rgba(8, 40, 87, 0.08);
}

@media (prefers-reduced-motion: reduce) {
  .stat-box {
    transition: none;
  }

  .stat-box:hover {
    transform: none;
  }

  .rank-item .rk-bar {
    transition: none;
  }
}

.stat-box .sb-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}

.stat-box.total .sb-icon {
  background: var(--primary-faint);
  color: var(--primary);
}

.stat-box.power .sb-icon {
  background: color-mix(in srgb, var(--color-warning) 18%, #ffffff);
  color: var(--cat-power);
}

.stat-box.comm .sb-icon {
  background: var(--primary-faint);
  color: var(--cat-comm);
}

.stat-box.volt .sb-icon {
  background: color-mix(in srgb, #e53935 14%, #ffffff);
  color: var(--cat-volt);
}

.stat-box.temp .sb-icon {
  background: color-mix(in srgb, #7c3aed 14%, #ffffff);
  color: var(--cat-temp);
}

.stat-box .sb-body {
  flex: 1;
}

.stat-box .sb-val {
  font-size: 26px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.2;
}

.stat-box .sb-label {
  font-size: 12px;
  color: var(--text-muted);
  margin-top: 2px;
}

.stat-box .sb-trend {
  position: absolute;
  top: 12px;
  right: 14px;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 8px;
}

.sb-trend.up {
  color: var(--danger);
  background: color-mix(in srgb, var(--color-error) 8%, #ffffff);
}

.sb-trend.down {
  color: var(--success);
  background: color-mix(in srgb, var(--color-success) 10%, #ffffff);
}

.card {
  background: var(--card-bg);
  border-radius: 12px;
  border: 1px solid #fff;
  box-shadow: var(--shadow-card);
  backdrop-filter: blur(6px);
  padding: 18px 20px;
  margin-bottom: 14px;
}

.row-2 .card {
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
}

.card-head {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-head .sub {
  font-size: 12px;
  color: var(--text-muted);
  font-weight: 400;
}

.chart-box {
  height: 360px;
  width: 100%;
  flex: 1;
}

.row-2 {
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 14px;
  margin-bottom: 14px;
}

.rank-list {
  list-style: none;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin: 0;
  padding: 0;
}

.rank-item {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--color-td-border);
}

.rank-item:last-child {
  border-bottom: none;
}

.rank-item .rk {
  width: 22px;
  height: 22px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  margin-right: 10px;
  flex-shrink: 0;
  background: var(--surface-muted);
  color: var(--text-secondary);
}

.rank-item:nth-child(1) .rk {
  background: var(--cat-power);
  color: #fff;
}

.rank-item:nth-child(2) .rk {
  background: var(--primary);
  color: #fff;
}

.rank-item:nth-child(3) .rk {
  background: var(--secondary);
  color: #fff;
}

.rank-item .rk-info {
  flex: 1;
  min-width: 0;
}

.rank-item .rk-name {
  font-size: 13px;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rank-item .rk-bar-wrap {
  height: 4px;
  background: var(--surface-muted);
  border-radius: 2px;
  margin-top: 4px;
  overflow: hidden;
}

.rank-item .rk-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.8s;
}

.rank-item .rk-val {
  font-size: 16px;
  font-weight: 700;
  margin-left: 14px;
  min-width: 32px;
  text-align: right;
}

.cat-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
}

@media (max-width: 1280px) {
  .stats-row {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .row-2 {
    grid-template-columns: 1fr;
  }
}
</style>
